import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { countLeadsByCode, createTrackedLink, getChannel, listTrackedLinks } from '../lib/api';
import { friendlyError, copyText } from '../lib/format';
import { PLACEMENTS, prefilledText, waLink } from '../lib/links';
import type { TrackedLink, WhatsAppChannel } from '../lib/types';
import { Button, Empty, ErrorText, Modal, PageHeader, SelectField, TextField } from '../components/ui';

export function Links() {
  const { projects, members, isManager, me } = useAuth();
  const [links, setLinks] = useState<TrackedLink[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [channel, setChannel] = useState<WhatsAppChannel | null>(null);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    const [ls, cs, ch] = await Promise.all([listTrackedLinks(), countLeadsByCode().catch(() => ({})), getChannel().catch(() => null)]);
    setLinks(ls); setCounts(cs); setChannel(ch);
  }, []);
  useEffect(() => { load(); }, [load]);

  const projectName = (id: string | null) => projects.find((p) => p.id === id)?.name ?? null;
  const memberName = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? 'No rep';
  const shown = isManager ? links : links.filter((l) => l.member_id === me?.id);

  async function copy(text: string, key: string) {
    await copyText(text); setCopied(key); setTimeout(() => setCopied(''), 2000);
  }

  return (
    <div>
      <PageHeader title="Tracked links"
        subtitle="One link per post and rep. A buyer who taps it arrives with the post, project and rep already on the lead."
        action={<Button variant="primary" onClick={() => setAdding(true)}>New link</Button>} />

      <div className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${channel ? 'border-ok bg-okSoft text-ok' : 'border-warn bg-warnSoft text-warn'}`}>
        {channel
          ? `WhatsApp connected: ${channel.display_number}. Links open a real chat with your desk.`
          : 'WhatsApp is not connected yet, so links work in the simulator only. Connecting a number later changes nothing else.'}
      </div>

      {shown.length === 0 ? <Empty title="No tracked links yet">Make one for each post or placement you share.</Empty> : (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {shown.map((l) => {
            const text = prefilledText(projectName(l.project_id), l.code);
            return (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium"><span className="font-mono">{l.code}</span> · {projectName(l.project_id) ?? 'Any project'}</p>
                  <p className="text-sm text-muted">
                    {l.channel_label ?? 'Post'} · {memberName(l.member_id)}{l.sub_broker ? ` · via ${l.sub_broker}` : ''}{l.label ? ` · ${l.label}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted">{counts[l.code] ?? 0} lead{(counts[l.code] ?? 0) === 1 ? '' : 's'}</span>
                  {channel
                    ? <Button onClick={() => copy(waLink(channel.display_number, text), l.id)}>{copied === l.id ? 'Copied' : 'Copy link'}</Button>
                    : <Link to={`/simulator?code=${l.code}`} className="rounded-lg border border-line px-3.5 py-2 text-sm font-medium hover:border-muted">Try in simulator</Link>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {adding && <NewLinkModal onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
    </div>
  );
}

function NewLinkModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { projects, members, isManager, me } = useAuth();
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const [memberId, setMemberId] = useState(me?.id ?? '');
  const [placement, setPlacement] = useState(PLACEMENTS[0]);
  const [subBroker, setSubBroker] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await createTrackedLink({ projectId: projectId || null, memberId: memberId || null, channelLabel: placement, subBroker, label });
      onSaved();
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="New tracked link">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <SelectField label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Any project</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </SelectField>
        <SelectField label="Where it will be shared" value={placement} onChange={(e) => setPlacement(e.target.value)}>
          {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
        </SelectField>
        {isManager ? (
          <SelectField label="Rep who shares it" value={memberId} onChange={(e) => setMemberId(e.target.value)}
            hint="Buyers from this link go to this rep first, and the rep keeps the credit.">
            <option value="">No rep (firm link)</option>
            {members.filter((m) => m.active).map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </SelectField>
        ) : <p className="text-sm text-muted">This link is yours: buyers from it come to you.</p>}
        <TextField label="Sub-broker (optional)" value={subBroker} onChange={(e) => setSubBroker(e.target.value)} placeholder="Sharma Properties" />
        <TextField label="Note (optional)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Site-visit weekend, 8 Nov" />
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Creating…' : 'Create link'}</Button>
        </div>
      </form>
    </Modal>
  );
}
